import React, { useState, useEffect, useMemo, useCallback } from "react";
import { EntityReference, type FireCMSContext } from "@firecms/core";
import { Button } from "@firecms/ui";
import { fieldCollection } from "../collections/field";
import { locationsCollection, LOCATIONS_PATH, LOCATIONS_FETCH_LIMIT } from "../collections/locations";
import { generatePath, generateTitle, generateDescription } from "../utils/pathGeneration";

// DR-408: resolved location entity data used to build the denormalised
// filter_fields.location map on import
interface ResolvedLocation {
    id: string;
    name: string;
    latlng: any;
    default_range_miles: number;
}

interface ImportDirectoriesModalProps {
    context: FireCMSContext;
    onClose: () => void;
}

interface ParsedRow {
    id: string;
    raw: Record<string, string>;
    translated: {
        title: string;
        clean_url: string;
        commitment_type: string | null;
        function: string | null;
        function_focus: string | null;
        industry: string | null;
        location: ResolvedLocation | null;
        description: string;
    };
    errors: string[];
    isValid: boolean;
}

export function ImportDirectoriesModal({ context, onClose }: ImportDirectoriesModalProps) {
    const [loadingMetadata, setLoadingMetadata] = useState(true);
    const [metadataError, setMetadataError] = useState<string | null>(null);
    const [inputText, setInputText] = useState("");
    const [fileName, setFileName] = useState<string | null>(null);
    const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
    const [activeTab, setActiveTab] = useState<"paste" | "upload">("paste");
    const [importError, setImportError] = useState<string | null>(null);
    const [parseWarning, setParseWarning] = useState<string | null>(null);

    const isMounted = React.useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Lookups caches
    const [lookups, setLookups] = useState<{
        commitment_type: Record<string, string>;
        function: Record<string, string>;
        function_focus: Record<string, Record<string, string>>;
        industry: Record<string, string>;
        location: Record<string, ResolvedLocation>;
    }>({
        commitment_type: {},
        function: {},
        function_focus: {},
        industry: {},
        location: {},
    });

    // On mount, load all metadata lookups in parallel
    useEffect(() => {
        let active = true;
        const loadMetadata = async () => {
            try {
                setLoadingMetadata(true);
                const fetchField = async (id: string) => {
                    const entity = await context.dataSource.fetchEntity({
                        path: "field",
                        entityId: id,
                        collection: fieldCollection,
                    });
                    return entity?.values || {};
                };

                // DR-408: locations come from setting/directory/locations,
                // not the legacy field/location doc
                const fetchLocations = async (): Promise<Record<string, ResolvedLocation>> => {
                    const entities = await context.dataSource.fetchCollection({
                        path: LOCATIONS_PATH,
                        collection: locationsCollection as any,
                        limit: LOCATIONS_FETCH_LIMIT
                    });
                    const reverse: Record<string, ResolvedLocation> = {};
                    entities
                        .filter((e: any) => e.values?.enabled !== false)
                        .forEach((e: any) => {
                            const v = e.values || {};
                            const resolved: ResolvedLocation = {
                                id: e.id,
                                name: v.name || "",
                                latlng: v.latlng,
                                default_range_miles: typeof v.default_range_miles === "number" ? v.default_range_miles : 0
                            };
                            if (v.name) reverse[String(v.name).trim().toLowerCase()] = resolved;
                            // the document ID is the location slug
                            reverse[String(e.id).trim().toLowerCase()] = resolved;
                        });
                    return reverse;
                };

                const [commitmentData, functionData, focusData, industryData, locationLookup] = await Promise.all([
                    fetchField("commitment_type"),
                    fetchField("function"),
                    fetchField("function_focus"),
                    fetchField("industry"),
                    fetchLocations(),
                ]);

                if (!active) return;

                // Helper to reverse map key_lookups / key_lookup
                const makeReverseLookup = (data: any) => {
                    const lookup = data.key_lookups || data.key_lookup || {};
                    const reverse: Record<string, string> = {};
                    Object.entries(lookup).forEach(([key, labelOrObj]) => {
                        if (typeof labelOrObj === "string") {
                            reverse[labelOrObj.trim().toLowerCase()] = key;
                        }
                    });
                    if (Array.isArray(data.key_value)) {
                        data.key_value.forEach((item: any) => {
                            if (item?.key && item?.label) {
                                reverse[item.label.trim().toLowerCase()] = item.key;
                            }
                        });
                    }
                    return reverse;
                };

                // Specific nested reverse lookup for function_focus
                const makeReverseFunctionFocus = (data: any) => {
                    const lookup = data.key_lookups || data.key_lookup || {};
                    const reverseFocus: Record<string, Record<string, string>> = {};
                    Object.entries(lookup).forEach(([parentKey, nestedObj]: [string, any]) => {
                        if (nestedObj && typeof nestedObj === "object") {
                            const nestedReverse: Record<string, string> = {};
                            Object.entries(nestedObj).forEach(([childKey, childValue]) => {
                                if (typeof childValue === "string") {
                                    nestedReverse[childValue.trim().toLowerCase()] = childKey;
                                }
                            });
                            reverseFocus[parentKey.trim().toLowerCase()] = nestedReverse;
                        }
                    });
                    return reverseFocus;
                };

                setLookups({
                    commitment_type: makeReverseLookup(commitmentData),
                    function: makeReverseLookup(functionData),
                    function_focus: makeReverseFunctionFocus(focusData),
                    industry: makeReverseLookup(industryData),
                    location: locationLookup,
                });
            } catch (err: any) {
                console.error("Error loading import metadata:", err);
                if (active) {
                    setMetadataError("Failed to fetch system categories and fields. Please try again.");
                }
            } finally {
                if (active) {
                    setLoadingMetadata(false);
                }
            }
        };

        loadMetadata();
        return () => {
            active = false;
        };
    }, [context.dataSource]);

    // Parse and Validate raw spreadsheet content
    const handleProcessInput = useCallback((text: string) => {
        if (!text.trim()) {
            setParsedRows([]);
            setParseWarning(null);
            return;
        }

        const data = parseCSVOrTSV(text);
        if (data.length < 2) {
            setParsedRows([]);
            setParseWarning(null);
            return;
        }

        // Header normalization (lowercase and trim)
        const headers = data[0].map(h => h.toLowerCase().trim());
        const allRows = data.slice(1);

        const MAX_IMPORT_ROWS = 1000;
        let rows = allRows;
        if (allRows.length > MAX_IMPORT_ROWS) {
            setParseWarning(`Input contains ${allRows.length} rows. Only the first ${MAX_IMPORT_ROWS} will be processed.`);
            rows = allRows.slice(0, MAX_IMPORT_ROWS);
        } else {
            setParseWarning(null);
        }

        const newParsedRows: ParsedRow[] = rows.map((row, index) => {
            const rawMap: Record<string, string> = {};
            headers.forEach((header, idx) => {
                rawMap[header] = row[idx] || "";
            });

            // Extract cells using multiple common header synonyms
            const rawTitle = (rawMap["directory page name"] || rawMap["title"] || rawMap["name"] || "").trim();
            const rawCommitment = (rawMap["commitment type"] || rawMap["commitment"] || "").trim();
            const rawFunction = (rawMap["function"] || rawMap["category"] || "").trim();
            const rawFocus = (rawMap["function focus"] || rawMap["focus"] || "").trim();
            const rawIndustry = (rawMap["industry"] || "").trim();
            const rawLocation = (rawMap["location"] || "").trim();
            const rawDescription = (rawMap["description"] || rawMap["desc"] || "").trim();

            const errors: string[] = [];

            // At least one of the core fields must be present
            const hasAnyCoreField = rawCommitment || rawFunction || rawIndustry || rawLocation;
            if (!hasAnyCoreField) {
                errors.push("Row must have at least one filter field (Commitment, Function, Industry, or Location).");
            }

            // 1. Resolve Commitment Slug
            let commitmentSlug: string | null = null;
            if (rawCommitment) {
                commitmentSlug = lookups.commitment_type[rawCommitment.toLowerCase()] || null;
                if (!commitmentSlug) {
                    errors.push(`Commitment Type '${rawCommitment}' is not a valid selection.`);
                }
            }

            // 2. Resolve Function Slug
            let functionSlug: string | null = null;
            if (rawFunction) {
                functionSlug = lookups.function[rawFunction.toLowerCase()] || null;
                if (!functionSlug) {
                    errors.push(`Function '${rawFunction}' is not a valid selection.`);
                }
            }

            // 3. Resolve Function Focus Slug (dependent on Function slug)
            let focusSlug: string | null = null;
            if (rawFocus) {
                if (functionSlug) {
                    const nestedFocusMap = lookups.function_focus[functionSlug.toLowerCase()];
                    if (nestedFocusMap) {
                        focusSlug = nestedFocusMap[rawFocus.toLowerCase()] || null;
                    }
                    if (!focusSlug) {
                        errors.push(`Function Focus '${rawFocus}' is not valid under Function '${rawFunction}'.`);
                    }
                } else {
                    errors.push(`Cannot resolve Function Focus '${rawFocus}' because Function is empty or invalid.`);
                }
            }

            // 4. Resolve Industry Slug
            let industrySlug: string | null = null;
            if (rawIndustry) {
                industrySlug = lookups.industry[rawIndustry.toLowerCase()] || null;
                if (!industrySlug) {
                    errors.push(`Industry '${rawIndustry}' is not a valid selection.`);
                }
            }

            // 5. Resolve Location (DR-408: matches enabled locations by name or slug)
            let resolvedLocation: ResolvedLocation | null = null;
            if (rawLocation) {
                resolvedLocation = lookups.location[rawLocation.toLowerCase()] || null;
                if (!resolvedLocation) {
                    errors.push(`Location '${rawLocation}' does not match any enabled directory location.`);
                }
            }

            // Generate Title & Clean URL using labels from the spreadsheet row
            const titleLabel = rawTitle || generateTitle(rawCommitment || undefined, rawFocus || rawFunction || undefined, rawIndustry || undefined, resolvedLocation?.name);
            const description = rawDescription || generateDescription(rawCommitment || undefined, rawFocus || rawFunction || undefined, rawIndustry || undefined, resolvedLocation?.name);
            const pathSlug = generatePath(
                commitmentSlug || undefined,
                focusSlug || functionSlug || undefined,
                industrySlug || undefined,
                resolvedLocation?.id || undefined
            );

            // Generate stable ID based on row values or unique content properties to avoid reconciliation issues
            const uniqueId = `row-${index}-${pathSlug}`;

            return {
                id: uniqueId,
                raw: rawMap,
                translated: {
                    title: titleLabel,
                    clean_url: pathSlug,
                    commitment_type: commitmentSlug,
                    function: functionSlug,
                    function_focus: focusSlug,
                    industry: industrySlug,
                    location: resolvedLocation,
                    description,
                },
                errors,
                isValid: errors.length === 0,
            };
        });

        setParsedRows(newParsedRows);
    }, [lookups]);

    // File Drop/Upload Handler
    const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
            if (!isMounted.current) return;
            const text = event.target?.result as string;
            setInputText(text);
            handleProcessInput(text);
        };
        reader.readAsText(file);
    }, [handleProcessInput]);

    // Execute Import in chunked parallel batches (e.g. 25 at a time) to eliminate serial latency
    const handleImport = useCallback(async () => {
        const validRows = parsedRows.filter(r => r.isValid);
        if (validRows.length === 0 || importing) return;

        setImporting(true);
        setImportError(null);
        setImportProgress({ current: 0, total: validRows.length });

        const CHUNK_SIZE = 25;
        let successCount = 0;
        const allFailures: string[] = [];

        try {
            for (let i = 0; i < validRows.length; i += CHUNK_SIZE) {
                if (!isMounted.current) return;
                const chunk = validRows.slice(i, i + CHUNK_SIZE);
                
                // Construct and trigger parallel save calls for the current chunk
                const savePromises = chunk.map(row => {
                    const entityValues = {
                        filter_fields: {
                            commitment_type: row.translated.commitment_type,
                            function: row.translated.function,
                            function_focus: row.translated.function_focus,
                            industry: row.translated.industry,
                            // DR-408: denormalised map shape { ref, name, latlng, range }
                            location: row.translated.location ? {
                                ref: new EntityReference(row.translated.location.id, LOCATIONS_PATH),
                                name: row.translated.location.name,
                                latlng: row.translated.location.latlng,
                                range: row.translated.location.default_range_miles
                            } : null,
                        },
                        override_clean_url: false,
                        clean_url: row.translated.clean_url,
                        title: row.translated.title,
                        description: row.translated.description,
                        enabled: true,
                        page_size: 20,
                        query: "",
                        filter: "",
                        boost_specs: [],
                        created_at: new Date(),
                        updated_at: new Date(),
                    };

                    return context.dataSource.saveEntity({
                        path: "directory",
                        values: entityValues,
                        status: "new",
                    });
                });

                // Wait for all saves in the current chunk using Promise.allSettled
                const results = await Promise.allSettled(savePromises);
                
                if (!isMounted.current) return;

                results.forEach(r => {
                    if (r.status === "fulfilled") {
                        successCount++;
                    } else {
                        allFailures.push((r as PromiseRejectedResult).reason?.message || String((r as PromiseRejectedResult).reason));
                    }
                });

                // Update precise local progress, bypassing any stale async state closures
                setImportProgress({ current: successCount, total: validRows.length });
            }

            if (!isMounted.current) return;

            // Handle overall completion status
            if (allFailures.length > 0) {
                setImportError(`Import completed with ${allFailures.length} error(s) after ${successCount} successful writes. Errors: ${allFailures.slice(0, 3).join("; ")}${allFailures.length > 3 ? "…" : ""}`);
            } else {
                onClose();
            }
        } catch (err: any) {
            console.error("Error during batch import:", err);
            if (isMounted.current) {
                setImportError(`Import failed after writing ${successCount} of ${validRows.length} records: ${err?.message || err}`);
            }
        } finally {
            if (isMounted.current) {
                setImporting(false);
            }
        }
    }, [parsedRows, importing, onClose, context.dataSource]);

    const { validCount, invalidCount } = useMemo(() => {
        let valids = 0;
        let invalids = 0;
        parsedRows.forEach(r => {
            if (r.isValid) valids++;
            else invalids++;
        });
        return { validCount: valids, invalidCount: invalids };
    }, [parsedRows]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 font-sans text-gray-800">
                
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Bulk Import Directories</h2>
                        <p className="text-xs text-gray-500 mt-1">Import up to 1,000 directories from Excel, Google Sheets, or CSV files.</p>
                    </div>
                    <button 
                        onClick={onClose} 
                        disabled={importing}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Main Content */}
                {loadingMetadata ? (
                    <div className="flex-grow flex flex-col items-center justify-center p-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                        <span className="text-sm text-gray-500 mt-4 font-medium">Fetching field lookup configurations...</span>
                    </div>
                ) : metadataError ? (
                    <div className="flex-grow flex flex-col items-center justify-center p-12 text-center">
                        <div className="bg-red-50 text-red-600 p-3 rounded-full mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <span className="text-base font-semibold text-gray-900">{metadataError}</span>
                        <Button variant="outlined" size="small" className="mt-4" onClick={onClose}>Close</Button>
                    </div>
                ) : importing ? (
                    <div className="flex-grow flex flex-col items-center justify-center p-12">
                        <div className="w-full max-w-md bg-gray-100 rounded-full h-3 overflow-hidden mb-4">
                            <div 
                                className="bg-indigo-600 h-full transition-all duration-300 rounded-full"
                                style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                            />
                        </div>
                        <span className="text-lg font-bold text-gray-900">Importing Directories...</span>
                        <span className="text-sm text-gray-500 mt-1">Processed {importProgress.current} of {importProgress.total} records</span>
                    </div>
                ) : (
                    <div className="flex-grow flex flex-col overflow-hidden">
                        
                        {/* Tabs */}
                        <div className="flex border-b border-gray-100 px-6">
                            <button
                                className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors ${activeTab === "paste" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                                onClick={() => { setActiveTab("paste"); setFileName(null); setParsedRows([]); setInputText(""); }}
                            >
                                Copy-Paste Sheets Rows
                            </button>
                            <button
                                className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors ${activeTab === "upload" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                                onClick={() => { setActiveTab("upload"); setParsedRows([]); setInputText(""); }}
                            >
                                Upload CSV/TSV File
                            </button>
                        </div>

                        {/* Input Panel */}
                        <div className="p-6 flex-shrink-0 bg-gray-50/50 border-b border-gray-100">
                            {activeTab === "paste" ? (
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Paste Spreadsheet Rows</label>
                                    <textarea
                                        rows={4}
                                        value={inputText}
                                        onChange={(e) => { setInputText(e.target.value); handleProcessInput(e.target.value); }}
                                        placeholder="Include header row. Example:&#13;Commitment Type&#9;Function&#9;Function Focus&#9;Industry&#9;Location&#13;Full Time&#9;Engineering&#9;Platform&#9;SaaS&#9;New York"
                                        className="w-full p-3 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs font-mono bg-white"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Upload Data File</label>
                                    <div className="flex items-center justify-center w-full">
                                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                <p className="mb-1 text-sm text-gray-700 font-semibold">Click to upload or drag & drop</p>
                                                <p className="text-xs text-gray-500">CSV, TSV, or TXT file (with headers)</p>
                                            </div>
                                            <input type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={handleFileUpload} />
                                        </label>
                                    </div>
                                    {fileName && (
                                        <div className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-md inline-block">
                                            📁 {fileName}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Preview / Validation Section */}
                        <div className="flex-grow overflow-auto p-6 min-h-[200px]">
                            {parsedRows.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center py-8">
                                    <p className="text-sm font-semibold text-gray-500">No data loaded.</p>
                                    <p className="text-xs text-gray-400 mt-1 max-w-sm">Provide data above with matching column headers to view a validation preview.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                     <div className="flex items-center gap-3">
                                         <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Validation Status:</span>
                                         <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded-full">{validCount} Ready to Import</span>
                                         {invalidCount > 0 && (
                                             <span className="bg-red-100 text-red-800 text-xs font-bold px-2.5 py-0.5 rounded-full">{invalidCount} Skipped (Has Errors)</span>
                                         )}
                                     </div>

                                     {parseWarning && (
                                         <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-xs font-semibold flex items-center gap-2">
                                             ⚠️ {parseWarning}
                                         </div>
                                     )}

                                      {/* Scrollable Preview Table */}
                                      <div className="border border-gray-100 rounded-lg overflow-x-auto shadow-sm max-w-full">
                                         <table className="min-w-full divide-y divide-gray-100 text-left text-xs">
                                             <thead className="bg-gray-50 font-semibold text-gray-700 uppercase tracking-wider">
                                                 <tr>
                                                     <th className="px-4 py-2 border-r border-gray-100">Status</th>
                                                     <th className="px-4 py-2 border-r border-gray-100">Clean URL (Path)</th>
                                                     <th className="px-4 py-2 border-r border-gray-100">Title</th>
                                                     <th className="px-4 py-2 border-r border-gray-100">Commitment</th>
                                                     <th className="px-4 py-2 border-r border-gray-100">Function</th>
                                                     <th className="px-4 py-2 border-r border-gray-100">Focus</th>
                                                      <th className="px-4 py-2 border-r border-gray-100">Industry</th>
                                                      <th className="px-4 py-2 border-r border-gray-100">Location</th>
                                                      <th className="px-4 py-2 border-gray-100">Description</th>
                                                  </tr>
                                             </thead>
                                             <tbody className="divide-y divide-gray-100 bg-white">
                                                 {parsedRows.map((row) => (
                                                     <tr key={row.id} className={row.isValid ? "hover:bg-green-50/20" : "bg-red-50/10 hover:bg-red-50/20"}>
                                                        <td className="px-4 py-2 border-r border-gray-100 font-medium whitespace-nowrap">
                                                            {row.isValid ? (
                                                                <span className="text-green-600 font-semibold flex items-center gap-1">🟢 Ready</span>
                                                            ) : (
                                                                <span className="text-red-600 font-semibold flex flex-col gap-1">
                                                                    <span>🔴 Skipped</span>
                                                                    {row.errors.map((e, idx) => (
                                                                        <span key={idx} className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded-md leading-normal font-normal max-w-xs whitespace-normal">{e}</span>
                                                                    ))}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 border-r border-gray-100 font-mono text-[10px] text-gray-600 max-w-xs truncate">{row.translated.clean_url || "-"}</td>
                                                        <td className="px-4 py-2 border-r border-gray-100 max-w-xs truncate font-medium text-gray-950">{row.translated.title || "-"}</td>
                                                         <td className="px-4 py-2 border-r border-gray-100 whitespace-nowrap text-gray-700">
                                                            {row.translated.commitment_type ? (
                                                                <div className="flex flex-col">
                                                                    <span className="font-semibold text-gray-950">{row.raw["commitment type"] || row.raw["commitment"]}</span>
                                                                    <span className="text-[10px] text-gray-400 font-mono">({row.translated.commitment_type})</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 font-normal italic">None</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 border-r border-gray-100 whitespace-nowrap text-gray-700">
                                                            {row.translated.function ? (
                                                                <div className="flex flex-col">
                                                                    <span className="font-semibold text-gray-950">{row.raw["function"] || row.raw["category"]}</span>
                                                                    <span className="text-[10px] text-gray-400 font-mono">({row.translated.function})</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 font-normal italic">None</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 border-r border-gray-100 whitespace-nowrap text-gray-700">
                                                            {row.translated.function_focus ? (
                                                                <div className="flex flex-col">
                                                                    <span className="font-semibold text-gray-950">{row.raw["function focus"] || row.raw["focus"]}</span>
                                                                    <span className="text-[10px] text-gray-400 font-mono">({row.translated.function_focus})</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 font-normal italic">None</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 border-r border-gray-100 whitespace-nowrap text-gray-700">
                                                            {row.translated.industry ? (
                                                                <div className="flex flex-col">
                                                                    <span className="font-semibold text-gray-950">{row.raw["industry"]}</span>
                                                                    <span className="text-[10px] text-gray-400 font-mono">({row.translated.industry})</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 font-normal italic">None</span>
                                                            )}
                                                        </td>
                                                         <td className="px-4 py-2 border-r border-gray-100 whitespace-nowrap text-gray-700">
                                                             {row.translated.location ? (
                                                                 <div className="flex flex-col">
                                                                     <span className="font-semibold text-gray-950">{row.translated.location.name}</span>
                                                                     <span className="text-[10px] text-gray-400 font-mono">
                                                                         ({row.translated.location.id} · {row.translated.location.default_range_miles} mi)
                                                                     </span>
                                                                 </div>
                                                             ) : (
                                                                 <span className="text-gray-400 font-normal italic">None</span>
                                                             )}
                                                         </td>
                                                         <td className="px-4 py-2 text-gray-600 max-w-sm truncate text-[10px]">
                                                             {row.translated.description || <span className="italic text-gray-400">None</span>}
                                                         </td>
                                                     </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-3 bg-gray-50/70">
                            {importError && (
                                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 rounded-lg text-xs font-semibold flex items-center gap-2">
                                    ❌ {importError}
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 font-medium">Rows with validation errors are automatically ignored.</span>
                                <div className="flex gap-3">
                                    <Button variant="outlined" size="small" onClick={onClose} disabled={importing}>Cancel</Button>
                                    <Button 
                                        variant="filled" 
                                        size="small" 
                                        disabled={validCount === 0 || importing} 
                                        onClick={handleImport}
                                        style={{ backgroundColor: (validCount > 0 && !importing) ? "#4f46e5" : "#a5b4fc" }}
                                    >
                                        Import {validCount} Directories
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                )}

            </div>
        </div>
    );
}

// Pure helper function for CSV/TSV line parsing
function parseCSVOrTSV(text: string): string[][] {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return [];

    // Detect delimiter from the header row once and apply uniformly
    const delimiter = lines[0].includes("\t") ? "\t" : ",";
    const result: string[][] = [];

    for (const line of lines) {
        const row: string[] = [];
        let inQuotes = false;
        let currentValue = "";

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    currentValue += '"';
                    i++; // Skip double quote
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === delimiter && !inQuotes) {
                row.push(currentValue.trim());
                currentValue = "";
            } else {
                currentValue += char;
            }
        }
        row.push(currentValue.trim());
        result.push(row);
    }
    return result;
}
